import { useCallback, useMemo } from 'react'
import uniqBy from 'lodash/uniqBy'
import { mapPartnerData, mapWarehouseData } from '../../warehouse/queries/dataUtils'
import {
  type GetRetailersForPartnerQuery,
  LocationFeatureEnum,
  useGetRetailersForPartnerQuery,
} from '../../../../__codegen__/api'
import { WarehouseLocationFeature } from '../../../../legacy/common/warehouseLocations'
import { type PartnerType } from '../../../../exports/utils'
import { type WarehouseContextValue } from '../../warehouse/WarehouseContext'
import { hasOnlyRestrictedBanners } from '../../../../legacy/components/AccessControl/accessControl.utils'

export const useFetchPartner = id => {
  const {
    data: partnerData,
    error: partnerError,
    loading: partnerLoading,
    refetch: partnerRefetch,
  } = useGetRetailersForPartnerQuery({
    variables: { partnerId: id?.toString() },
    skip: !id,
  })

  const partnerId = id?.toString()
  const featureToggles = partnerData?.partner?.featureToggles

  // convert array of (retailerId, featureToggles) tuples into a object (hash) of (retailerId, featureToggles)
  const featureTogglesByRetailer = useMemo(
    () =>
      (partnerData?.partner?.featureTogglesByRetailer || []).reduce(
        (acc, { retailerId, featureToggles }) => {
          acc[retailerId] = featureToggles
          return acc
        },
        {}
      ),
    [partnerData?.partner?.featureTogglesByRetailer]
  )

  const featureEntitlements = useMemo(() => {
    const featureEntitlementsAgg =
      partnerData?.partner?.retailers?.flatMap(e => e.featureEntitlements) || []

    return uniqBy(featureEntitlementsAgg, e => e.id)
  }, [partnerData])

  const locationFeatures = useMemo(() => {
    const locationFeaturesAgg =
      partnerData?.partner?.retailers?.flatMap(e => e.locationFeatures) || []

    return [...new Set(locationFeaturesAgg)].map(e => {
      if (e == LocationFeatureEnum.RetailerAlcoholDelivery) {
        return WarehouseLocationFeature.RetailerAlcoholDelivery
      } else {
        return WarehouseLocationFeature.RetailerPickup
      }
    })
  }, [partnerData])

  const partnerName = partnerData?.partner?.name
  const partnerType = partnerData?.partner?.partnerType

  /**
   * Combine the partner into a single unit with its locales + featureToggles + entitlements
   */
  const partner = useMemo(() => {
    if (!partnerData?.partner) {
      return null
    }

    const mappedPartner = mapPartnerData({
      partnerId: partnerId,
      partnerData: partnerData?.partner,
      localesData: null,
      featureEntitlements,
      featureToggles,
    })

    return mappedPartner
  }, [partnerData?.partner, partnerId, featureEntitlements, featureToggles])

  const error = partnerError
  const errorWhileRevalidating = false

  const warehouses = useMemo(
    () =>
      partnerData?.partner?.retailers.map(retailer => {
        const retailerWithFeatureToggles = {
          ...retailer,
          partner: {
            id: partnerId,
            name: partnerName,
            partnerType: partnerType,
          },
          featureToggles: featureTogglesByRetailer[retailer.id] || featureToggles,
        }
        const mappedRetailer = mapWarehouseData({
          retailerData: retailerWithFeatureToggles,
          localesData: [],
          locationsData: [],
        })

        const locationFeatures = retailer.locationFeatures?.map(e => {
          if (e == LocationFeatureEnum.RetailerAlcoholDelivery) {
            return WarehouseLocationFeature.RetailerAlcoholDelivery
          } else {
            return WarehouseLocationFeature.RetailerPickup
          }
        })

        return {
          ...mappedRetailer,
          context: {
            warehouse: mappedRetailer,
            locationFeatures,
            storeConfigurations: [],
            validId: true,
            partnerName: partnerName,
            partnerId: Number(partnerId),
            partnerType: partnerType,
            featureToggles: retailerWithFeatureToggles.featureToggles,
            featureEntitlements: retailer.featureEntitlements,
            loaded: true,
            loading: false,
            error: false,
            isRestricted: false,
          } as WarehouseContextValue,
          locationFeatures,
          storeConfigurations: [],
        }
      }),
    [
      featureToggles,
      featureTogglesByRetailer,
      partnerData?.partner?.retailers,
      partnerId,
      partnerName,
      partnerType,
    ]
  )

  const isRestricted = hasOnlyRestrictedBanners(warehouses)

  return {
    partner,
    partnerName,
    partnerType: partnerType as PartnerType,
    warehouses,
    featureToggles,
    featureEntitlements,
    locationFeatures,
    error,
    errorWhileRevalidating,
    loading: partnerLoading,
    refetch: partnerRefetch,
    isRestricted,
  }
}
